import { useState } from "react"
import { Box, Grid, IconButton } from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import NCText from "../../shared/NCText"
import NCTable from "../../shared/table/NCTable"
import NCTableStatus from "../../shared/table/NCTableStatus"
import { useGetDashboard } from "../../services/queries"
import { formatDate, formatAMPM } from "../../libs/utils"
import { DetailIcon } from "../../libs/icons"
import NotificationAlert from "../../components/notification/NotificationAlert"

const useStyles = makeStyles(theme => ({
  section: {
    marginBottom: theme.spacing(3)
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: 16,
    color: "#232323",
    marginBottom: theme.spacing(1.5)
  },
  card: {
    background: "#fff",
    border: "1px solid #EDEFF2",
    borderRadius: 12,
    padding: theme.spacing(2.5),
    height: "100%",
    boxSizing: "border-box"
  },
  statCard: {
    background: "#fff",
    border: "1px solid #EDEFF2",
    borderRadius: 12,
    padding: theme.spacing(2.5),
    height: "100%",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    transition: "box-shadow .2s ease, transform .2s ease",
    "&:hover": {
      boxShadow: "0 8px 24px rgba(16,24,40,0.08)",
      transform: "translateY(-2px)"
    }
  },
  statIconWrap: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
  },
  statValue: {
    display: "block",
    fontWeight: 700,
    fontSize: 30,
    color: "#111827",
    lineHeight: 1.1,
    letterSpacing: "-0.02em"
  },
  statLabel: {
    display: "block",
    fontSize: 13,
    fontWeight: 500,
    color: "#6B7280",
    marginTop: 2
  },
  statusRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 0",
    borderBottom: "1px solid #F0F0F0",
    "&:last-child": {
      borderBottom: "none",
      paddingBottom: 0
    },
    "&:first-child": {
      paddingTop: 0
    }
  },
  statusLeft: {
    display: "flex",
    alignItems: "center",
    gap: 8
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    flexShrink: 0
  },
  statusLabel: {
    fontSize: 14,
    color: "#232323"
  },
  statusCount: {
    fontSize: 14,
    fontWeight: 600,
    color: "#232323"
  },
  groupLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: "#9CA3AF",
    marginBottom: theme.spacing(1),
    textTransform: "uppercase",
    letterSpacing: "0.04em"
  },
  rideRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: "10px 0",
    borderBottom: "1px solid #F0F0F0",
    "&:last-child": {
      borderBottom: "none",
      paddingBottom: 0
    },
    "&:first-child": {
      paddingTop: 0
    }
  },
  rideTime: {
    minWidth: 70,
    fontSize: 13,
    fontWeight: 600,
    color: "#1EAAC4"
  },
  rideInfo: {
    flex: 1,
    minWidth: 0
  },
  rideClient: {
    fontSize: 14,
    fontWeight: 500,
    color: "#232323",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  rideMeta: {
    fontSize: 12,
    color: "#6B7280",
    marginTop: 2,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  empty: {
    fontSize: 13,
    color: "#9CA3AF",
    padding: theme.spacing(2, 0)
  },
  viewAll: {
    color: "#1EAAC4",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    border: "none",
    background: "none",
    padding: 0,
    "&:hover": {
      textDecoration: "underline"
    }
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1.5)
  },
  // Scoped to the dashboard's Recent Requests table only — leaves
  // the shared NCTable/table.scss styling untouched elsewhere.
  recentTable: {
    "& .table-responsive": {
      overflowX: "auto"
    },
    "& table td": {
      paddingTop: theme.spacing(1.5),
      paddingBottom: theme.spacing(1.5),
      verticalAlign: "middle"
    },
    "& table th": {
      paddingTop: theme.spacing(0.5),
      paddingBottom: theme.spacing(1.25)
    },
    "& table tbody tr": {
      transition: "background-color .15s ease"
    },
    "& table tbody tr:hover": {
      backgroundColor: "#F7F8FA"
    }
  }
}))

// Match NCTableStatus colors used across the app
const STATUS_COLOR = {
  pending: "#FF9800",
  approved: "#1DB954",
  on_hold: "#FF9800",
  cancelled: "#E22134",
  scheduled: "#1EAAC4",
  in_progress: "#1EAAC4",
  completed: "#1DB954"
}

// Self-contained icons so we control color via currentColor
const svgProps = {
  width: 22,
  height: 22,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.9,
  strokeLinecap: "round",
  strokeLinejoin: "round"
}

const RequestsIcon = () => (
  <svg {...svgProps}>
    <rect x="4" y="3" width="16" height="18" rx="2" />
    <path d="M8 8h8M8 12h8M8 16h5" />
  </svg>
)
const ClientsIcon = () => (
  <svg {...svgProps}>
    <circle cx="9" cy="8" r="3.2" />
    <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" />
    <path d="M16 5a3.2 3.2 0 0 1 0 6.4" />
    <path d="M17.5 14.2c1.9.8 3 2.7 3 5.8" />
  </svg>
)
const DriversIcon = () => (
  <svg {...svgProps}>
    <path d="M5 13l1.4-4.2A2 2 0 0 1 8.3 7.4h7.4a2 2 0 0 1 1.9 1.4L19 13" />
    <path d="M4 13h16v3.5a1 1 0 0 1-1 1h-1.5a1 1 0 0 1-1-1V16H7.5v.5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V13z" />
    <circle cx="7.5" cy="15.2" r="1" />
    <circle cx="16.5" cy="15.2" r="1" />
  </svg>
)
const CompletedIcon = () => (
  <svg {...svgProps}>
    <circle cx="12" cy="12" r="9" />
    <path d="M8.3 12.3l2.5 2.5 4.9-5.4" />
  </svg>
)
const StatCard = ({ label, value, color, Icon }) => {
  const cls = useStyles()
  return (
    <Box className={cls.statCard}>
      <Box
        className={cls.statIconWrap}
        style={{ backgroundColor: `${color}1A`, color }}
      >
        <Icon />
      </Box>
      <Box>
        <NCText className={cls.statValue} translate={false}>
          {value ?? 0}
        </NCText>
        <NCText className={cls.statLabel}>{label}</NCText>
      </Box>
    </Box>
  )
}

const StatusRow = ({ label, count, color }) => {
  const cls = useStyles()
  return (
    <Box className={cls.statusRow}>
      <Box className={cls.statusLeft}>
        <Box className={cls.statusDot} style={{ backgroundColor: color }} />
        <NCText className={cls.statusLabel}>{label}</NCText>
      </Box>
      <NCText className={cls.statusCount} translate={false}>
        {count ?? 0}
      </NCText>
    </Box>
  )
}

const Dashboard = () => {
  const cls = useStyles()
  const { t } = useTranslation()
  const nav = useNavigate()
  const { data } = useGetDashboard()

  const d = data?.data?.data ?? {}
  const req = d.requests ?? {}
  const rides = d.rides_today ?? {}
  const recent = d.recent_requests ?? []
  const upcoming = d.upcoming_rides_today ?? []
  const upcomingTotal = d.upcoming_rides_today_total ?? upcoming.length
  const [upcomingPage, setUpcomingPage] = useState(1)
  const [upcomingPageSize, setUpcomingPageSize] = useState(5)
  const upcomingPageCount = Math.max(1, Math.ceil(upcoming.length / upcomingPageSize))
  const upcomingData = upcoming.slice(
    (upcomingPage - 1) * upcomingPageSize,
    upcomingPage * upcomingPageSize
  )

  const recentColumns = [
    { Header: "Request ID", accessor: "identifier" },
    { Header: "Submitter", accessor: "submitter_name" },
    {
      Header: "Status",
      accessor: "status_desc",
      Cell: ({ value }) => <NCTableStatus status={value} />
    },
    {
      Header: "Date",
      accessor: "created",
      Cell: ({ value }) => formatDate(value)
    },
    {
      Header: "",
      id: "action",
      disableSortBy: true,
      width: 48,
      accessor: cell => (
        <IconButton onClick={() => nav(`/requests/${cell.id}`)} size="small">
          <DetailIcon />
        </IconButton>
      )
    }
  ]

  return (
    <Grid container>
      {/* Header — same pattern as other pages */}
      <Grid item xs={12}>
        <Box mb={2}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item xs={12} md={4}>
              <NCText variant="h4">{t("navigation.dashboard")}</NCText>
            </Grid>
            <Grid item>
              <NotificationAlert />
            </Grid>
          </Grid>
        </Box>
      </Grid>

      {/* KPI row */}
      <Grid item xs={12} className={cls.section}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label={t("dashboard.total_requests")}
              value={req.total}
              color="#1EAAC4"
              Icon={RequestsIcon}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label={t("dashboard.active_clients")}
              value={d.clients?.total_active}
              color="#6366F1"
              Icon={ClientsIcon}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label={t("dashboard.active_drivers")}
              value={d.drivers?.total_active}
              color="#F59E0B"
              Icon={DriversIcon}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label={t("dashboard.completed_rides")}
              value={d.completed_rides_this_month}
              color="#1DB954"
              Icon={CompletedIcon}
            />
          </Grid>
        </Grid>
      </Grid>

      {/* Status + summary */}
      <Grid item xs={12} className={cls.section}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Box className={cls.card}>
              <NCText className={cls.sectionTitle}>
                {t("dashboard.requests_by_status")}
              </NCText>
              <StatusRow
                label={t("requests.pending")}
                count={req.pending}
                color={STATUS_COLOR.pending}
              />
              <StatusRow
                label={t("requests.approved")}
                count={req.approved}
                color={STATUS_COLOR.approved}
              />
              <StatusRow
                label={t("requests.on_hold")}
                count={req.on_hold}
                color={STATUS_COLOR.on_hold}
              />
              <StatusRow
                label="Cancelled"
                count={req.cancelled}
                color={STATUS_COLOR.cancelled}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box className={cls.card}>
              <NCText className={cls.sectionTitle}>
                {t("dashboard.todays_rides")}
              </NCText>
              <StatusRow
                label="Scheduled"
                count={rides.scheduled}
                color={STATUS_COLOR.scheduled}
              />
              <StatusRow
                label="In Progress"
                count={rides.in_progress}
                color={STATUS_COLOR.in_progress}
              />
              <StatusRow
                label="Completed"
                count={rides.completed}
                color={STATUS_COLOR.completed}
              />
              <StatusRow
                label="Cancelled"
                count={rides.cancelled}
                color={STATUS_COLOR.cancelled}
              />
            </Box>
          </Grid>
        </Grid>
      </Grid>

      {/* Upcoming rides */}
      <Grid item xs={12} className={cls.section}>
        <Box className={cls.card}>
          <Box className={cls.sectionHeader}>
            <NCText className={cls.sectionTitle} style={{ marginBottom: 0 }}>
              {t("dashboard.upcoming_rides")}
            </NCText>
            <NCText style={{ fontSize: 13, color: "#6B7280" }} translate={false}>
              {upcomingTotal}
            </NCText>
          </Box>
          {upcoming.length === 0 ? (
            <NCText className={cls.empty}>{t("dashboard.no_upcoming")}</NCText>
          ) : (
            <Box className={cls.recentTable}>
              <NCTable
                columns={[
                  { Header: "Time", accessor: "pickup_time", Cell: ({ value }) => value ? formatAMPM(value) : "—" },
                  { Header: "Client", accessor: "client_name" },
                  { Header: "Driver", accessor: "driver_name" },
                  { Header: "Status", accessor: "status_desc", Cell: ({ value }) => <NCTableStatus status={value} /> },
                ]}
                data={upcomingData}
                page={upcomingPage}
                onNext={() => setUpcomingPage(p => (p < upcomingPageCount ? p + 1 : p))}
                onPrev={() => setUpcomingPage(p => (p > 1 ? p - 1 : p))}
                pageCount={upcomingPageCount}
                gotoPage={setUpcomingPage}
                pageSize={upcomingPageSize}
                setPageSize={setUpcomingPageSize}
                inlineFooter
              />
            </Box>
          )}
        </Box>
      </Grid>

      {/* Recent requests */}
      <Grid item xs={12}>
        <Box className={cls.card}>
          <Box className={cls.sectionHeader}>
            <NCText className={cls.sectionTitle} style={{ marginBottom: 0 }}>
              {t("dashboard.recent_requests")}
            </NCText>
            <button
              type="button"
              className={cls.viewAll}
              onClick={() => nav("/requests")}
            >
              {t("dashboard.view_all")}
            </button>
          </Box>
          {recent.length === 0 ? (
            <NCText className={cls.empty}>{t("dashboard.no_recent")}</NCText>
          ) : (
            <Box className={cls.recentTable}>
              <NCTable
                columns={recentColumns}
                data={recent}
                hidePagination
                inlineFooter
              />
            </Box>
          )}
        </Box>
      </Grid>
    </Grid>
  )
}

export default Dashboard
